import moment from "moment";

import AdverseReactionCheckListView from "issara-sdk/apis/AdverseReactionCheckListView_apps_ADR";
import CheckDrugDuplicateDrugGroupView from "issara-sdk/apis/CheckDrugDuplicateDrugGroupView_apps_TPD";
import DoctorFeeOrderCreateUpdateList from "issara-sdk/apis/DoctorFeeOrderCreateUpdateList_apps_DFC";
import DoctorFeeOrderDetail from "issara-sdk/apis/DoctorFeeOrderDetail_apps_DFC";
import DoctorFeeOrderPreviewList from "issara-sdk/apis/DoctorFeeOrderPreviewList_apps_DFC";
import DoctorNoteOrderDetail from "issara-sdk/apis/DoctorNoteOrderDetail_apps_DPO";
import DoctorNoteOrderList from "issara-sdk/apis/DoctorNoteOrderList_apps_DPO";
import DoctorOrderCancel from "issara-sdk/apis/DoctorOrderCancel_apps_DPO";
import DrugDetail from "issara-sdk/apis/DrugDetail_apps_TPD";
import DrugDosingRecommendationList from "issara-sdk/apis/DrugDosingRecommendationList_apps_TPD";
import DrugDosingRecommendationRangeList from "issara-sdk/apis/DrugDosingRecommendationRangeList_apps_TPD";
import DrugInteractionCheck from "issara-sdk/apis/DrugInteractionCheck_apps_ADR";
import DrugLabInteractionCheck from "issara-sdk/apis/DrugLabInteractionCheck_apps_ADR";
import DrugOPDHomeOrderView from "issara-sdk/apis/DrugOPDHomeOrderView_apps_TPDM";
import DrugOPDOneDoseOrderView from "issara-sdk/apis/DrugOPDOneDoseOrderView_apps_TPDM";
import DrugOPDStatOrderView from "issara-sdk/apis/DrugOPDStatOrderView_apps_TPDM";
import DrugOrderEstimateView from "issara-sdk/apis/DrugOrderEstimateView_apps_TPD";
import ImagingOrderDetail from "issara-sdk/apis/ImagingOrderDetail_apps_IME";
import ImagingOrderList from "issara-sdk/apis/ImagingOrderList_apps_IME";
import CentralLabTestList from "issara-sdk/apis/CentralLabTestList_apps_LAB";
import MiscellaneousOrderListCreate from "issara-sdk/apis/MiscellaneousOrderListCreate_core";
import MiscellaneousPreviewList from "issara-sdk/apis/MiscellaneousPreviewList_core";
import PackageOrderWithItemList from "issara-sdk/apis/PackageOrderWithItemList_core";
import OrderSetTemplateDetail from "issara-sdk/apis/OrderSetTemplateDetail_apps_PTM";
import OrderSetTemplateList from "issara-sdk/apis/OrderSetTemplateList_apps_PTM";
import ProductForOrderList from "issara-sdk/apis/ProductForOrderList_core";
import SupplyList from "issara-sdk/apis/SupplyList_apps_MSD";
import SupplyOrderDetail from "issara-sdk/apis/SupplyOrderDetail_apps_MSD";
import SupplyOrderItemPreviewM from "issara-sdk/apis/SupplyOrderItemPreview_apps_MSDM";
import SupplyOrderList from "issara-sdk/apis/SupplyOrderList_apps_MSD";
import TreatmentOrderDetail from "issara-sdk/apis/TreatmentOrderDetail_apps_TRT";
import TreatmentOrderList from "issara-sdk/apis/TreatmentOrderList_apps_TRT";
import icd9cmKeyUp from "issara-sdk/apis/icd9cmKeyUp_core";
import type DrugInteractionGroupSerializer from "issara-sdk/types/DrugInteractionGroupSerializer_apps_ADR";
import type DrugSerializer from "issara-sdk/types/DrugSerializer_apps_TPD";

import type { OptionType } from "react-lib/apps/HISV3/common/CommonInterface";
import { formatDate, parseDate } from "react-lib/utils/dateUtils";

import {
  drugTypeOf,
  formatImagingNote,
  parseGroupDuplicate,
  pickRegimen,
  shortName,
  supplyTypeOf,
  withDoseWarning,
  type DrugOrderType,
  type LabTest,
} from "../../patient/ordersModelV4";
import {
  DEFAULT_FIELDS,
  NOTE_ITEM_NAME,
  drugTypeOfOrder,
  toCatalog,
  toCoreProcedure,
  toDrugItem,
  toOrderLines,
  toSetEntries,
  toSetTemplate,
  withDrugQuantity,
} from "../../patient/orderSheetModelV4";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  ADD: "ADD",
  EDIT: "EDIT",
  REMOVE: "REMOVE",
  DROP_ORDER: "DROP_ORDER",
  CONFIRM: "CONFIRM",
  SEARCH_ICD9: "SEARCH_ICD9",
  LOAD_ORDER: "LOAD_ORDER",
  LOAD_SETS: "LOAD_SETS",
  SAVE_SET: "SAVE_SET",
  ADD_ITEMS: "ADD_ITEMS",
  RESOLVE_ADR: "RESOLVE_ADR",
  RESOLVE_INTERACTION: "RESOLVE_INTERACTION",
} as const;

export const SEQUENCE_NAME = "OrderSheetV4" as const;

/** =============== Types and Interfaces ===============  */
export type SheetKind = "imaging" | "treatment" | "drug" | "supply" | "note" | "df" | "misc";

export type CatalogItem = {
  key: string;
  kind: SheetKind;
  id: number | null;
  name: string;
  price: number | null;
  raw: Record<string, any>;
};

export type SheetLine = {
  key: string;
  /** key ของรายการใน catalog */
  itemKey: string;
  kind: SheetKind;
  product: number | null;
  name: string;
  quantity: number;
  /** ราคาต่อหน่วยจาก master */
  masterPrice: number;
  priceUnit: number;
  fields: Record<string, string>;
  raw: Record<string, any>;
  /** Order ที่กำลังแก้ไข */
  orderId?: number;
  orderItemId?: number;
};

export type EditableKind = "imaging" | "treatment" | "note" | "drug" | "supply" | "df";

/** Order ที่ดึงกลับมาแก้ไขในรายการรอยืนยัน */
export type EditingOrder = { kind: EditableKind; orderId: number; drugType?: DrugOrderType };

/** รายการใน Order Set */
export type SetEntry = {
  key: string;
  kind: SheetKind | "lab";
  name: string;
  /** ราคารวมของรายการ */
  price: number;
  quantity: number;
  item?: CatalogItem;
  fields?: Record<string, string>;
  /** ข้อมูลแลปจาก central-lab-test */
  lab?: LabTest;
};

export type SaveSetResult = {
  count?: number;
  /** ชื่อรายการที่ Order set เก็บไม่ได้ */
  skipped?: string[];
  missing?: string[];
};

export type DoctorOption = { id: number; employee: number; name: string; providers: number[] };

export type DrugMaster = "unit" | "route" | "site" | "frequency" | "prn";

export type OrderMaster =
  | DrugMaster
  | "costCenter"
  | "patientAppType"
  | "appointmentForNote"
  | "drugAllergyReason"
  | "drugInteractReason";

export type OrderMasterOptions = Partial<Record<OrderMaster, OptionType[]>>;

/** สิทธิ์คงเหลือของสินค้าในแพ็กเกจที่ผู้ป่วยซื้อ */
export type PackageEntitlement = {
  product: number;
  packageName: string;
  left: number;
};

/** ผลตรวจแพ้ยาของยา 1 ตัว (adverse-reaction-list/check) */
export type AdrWarning = {
  product: number;
  name: string;
  /** สิ่งที่แพ้ จากบรรทัด ADR: ของ notes */
  adversary: string;
  notes: string;
  /** ห้ามสั่ง (status LOCKED) */
  locked: boolean;
};

/** รายการที่รอแพทย์ตัดสินเรื่องแพ้ยาก่อนเข้าใบคำสั่ง */
export type AdrAlert = {
  entries: SetEntry[];
  isDoctor?: boolean;
  warnings: AdrWarning[];
};

/** ยาตีกัน 1 กลุ่ม (drug-interaction/check) */
export type DrugInteraction = {
  id: number;
  members: number[];
  names: string[];
  severity?: number;
  affectSummary: string;
  management: string;
};

/** ยาที่มีผลกับผลแลปของผู้ป่วย (drug-lab-interaction/check) */
export type DrugLabInteraction = {
  product: number;
  name: string;
  summary: string;
  detail: string;
};

/** รายการที่รอแพทย์ตัดสินเรื่องยาตีกันก่อนเข้าใบคำสั่ง */
export type InteractionAlert = {
  entries: SetEntry[];
  isDoctor?: boolean;
  adr?: AdrDecision;
  drugs: DrugInteraction[];
  labs: DrugLabInteraction[];
};

export type GroupDuplicate = { group: string; names: string[] };

/** ขนาดยาสูงสุดต่อวันจาก Regimen ตามหน่วยที่สั่ง */
export type DoseRange = { unit: string; maxPerDay: number | null; unitName: string };

export type AdrChoice = "cancel" | "skip" | "order";

export type AdrDecision = { warnings: AdrWarning[]; choice: AdrChoice; reason?: string };

export type Icd9Option = {
  id: number;
  code: string;
  term: string;
  medterm: string;
  /** ข้อความใน combo */
  label: string;
};

export type State = Partial<{
  OrderSheetV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    encounterId: number | null;
    query: string;
    results: CatalogItem[];
    lines: SheetLine[];
    editing: EditingOrder[];
    doctorOptions: DoctorOption[];
    icd9Query: string;
    icd9Options: Icd9Option[];
    packages: PackageEntitlement[];
    /** รายการของแต่ละ Order Set ตาม id */
    sets: Record<number, SetEntry[]>;
    adrAlert: AdrAlert | null;
    interactionAlert: InteractionAlert | null;
    /** ผลตรวจแพ้ยาของผลค้นหา ตาม product */
    adrResults: Record<number, AdrWarning>;
    /** ข้อความเตือนยาซ้ำกลุ่มของรายการที่เพิ่งเพิ่ม */
    groupNotice: string | null;
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "selectedEncounter" | "selectedEmr" | "currentDoctor"
> &
  Partial<{ selectedProgressCycle: { id?: number } | null }>;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> }
  /** #86608-2: ยืนยัน/บันทึกคำสั่งบนหน้า OrderSheet -> MainHIS อ่านคิวใหม่ (case ใน MainHISController.tsx) */
  | { message: "HandleRefreshEncounter"; params: Record<string, unknown> };

export type Data = {
  division?: number;
  device?: number;
  masterData?: Record<string, any>;
};

export type SeqState = BaseSeqState<SequenceName> & { encounterId?: number };

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { query: string; kinds: SheetKind[] };
  [ACTIONS.ADD]: { item: CatalogItem; isDoctor?: boolean };
  [ACTIONS.EDIT]: {
    key: string;
    quantity?: number;
    priceUnit?: number;
    fields?: Record<string, string>;
  };
  [ACTIONS.REMOVE]: { key: string };
  [ACTIONS.DROP_ORDER]: { kind: SheetKind; orderId: number };
  [ACTIONS.CONFIRM]: { kinds: SheetKind[]; onDone?: (saved: SheetKind[]) => void };
  [ACTIONS.SEARCH_ICD9]: { query: string };
  [ACTIONS.LOAD_ORDER]: {
    kind: EditableKind;
    orderId: number;
    prices?: number[];
    specificType?: string;
    /** รายการ DF จาก doctorFeeList (ไม่ต้องโหลดซ้ำ) */
    fee?: Record<string, any>;
  };
  [ACTIONS.LOAD_SETS]: { ids: number[] };
  [ACTIONS.SAVE_SET]: {
    name: string;
    scope: "mine" | "clinic";
    doctorId?: number;
    /** รายการแลปในใบคำสั่ง (OrderCentralLab order_items) */
    labItems: Record<string, any>[];
    onDone: (result: SaveSetResult) => void;
  };
  [ACTIONS.ADD_ITEMS]: {
    entries: SetEntry[];
    isDoctor?: boolean;
    /** ผลที่แพทย์เลือกจาก dialog แพ้ยา */
    adr?: AdrDecision;
    /** เหตุผลที่สั่งต่อจาก dialog ยาตีกัน */
    interaction?: { drugs: DrugInteraction[]; labs: DrugLabInteraction[]; reason: string };
  };
  [ACTIONS.RESOLVE_ADR]: { choice: AdrChoice; reason?: string };
  [ACTIONS.RESOLVE_INTERACTION]: { choice: "cancel" | "order"; reason?: string };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type SaveSetParams = Params<Actions["SAVE_SET"]>;

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  OrderSheetV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** หมายเหตุการยกเลิก Order ที่แก้แล้วไม่มีรายการเหลือ */
export const EMPTY_EDIT_NOTE = "ยกเลิก (ไม่มีรายการเหลือ)";

export const SHEET_KINDS: SheetKind[] = [
  "imaging",
  "treatment",
  "drug",
  "supply",
  "note",
  "df",
  "misc",
];

export const PERFORMERS: [string, string][] = [
  ["DOCTOR", "ผู้ให้บริการ"],
  ["NURSE", "พยาบาล"],
  ["OTHER", "เจ้าหน้าที่"],
];

export const SUPPLY_USES: [string, string][] = [
  ["REFILL_OPD", "Refill (ใช้ที่คลินิก · เก็บเงินเพิ่มจากผู้ป่วย)"],
  ["COST_CENTER", "Cost center (เหมารวม · ไม่เก็บเงินเพิ่ม)"],
];

export const SEARCH_LIMIT = 20;

/** หมวดที่ค้นผ่าน core/product-for-order/ (supply ยังใช้ MSD/supply/) */
const PRODUCT_KINDS: Partial<Record<SheetKind, string>> = {
  imaging: "IMAGING",
  treatment: "TREATMENT",
  drug: "DRUG",
  df: "DOCTOR_FEE",
  misc: "MISCELLANEOUS",
};

export const EDITABLE_KINDS: EditableKind[] = [
  "imaging",
  "treatment",
  "note",
  "drug",
  "supply",
  "df",
];

const DRUG_MASTERS: DrugMaster[] = ["unit", "route", "site", "frequency", "prn"];

const DRUG_ORDER_APIS: Record<DrugOrderType, typeof DrugOPDHomeOrderView> = {
  HOME_OPD: DrugOPDHomeOrderView,
  ONE_DOSE: DrugOPDOneDoseOrderView,
  STAT: DrugOPDStatOrderView,
};

/** ประเภทรายการที่ Order set เก็บได้ */
const SET_KINDS: SheetKind[] = ["imaging", "treatment", "drug", "supply", "note", "df", "misc"];

/** key ใน errorMessage ของหมวดที่โหลด / บันทึกไม่สำเร็จ */
export const sheetErrorKey = (kind: SheetKind) => `${BTN_ACTS.CONFIRM}_${kind}`;

/** ล้าง error ของทุกหมวด */
const clearedSheetErrors = (errorMessage: Record<string, unknown> | undefined) => ({
  ...errorMessage,
  ...Object.fromEntries(SHEET_KINDS.map((kind) => [sheetErrorKey(kind), null])),
});

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: [
        ...["doctor", "costCenter", "drugAllergyReason", "drugInteractReason", ...DRUG_MASTERS],
      ].map((name) => [name, {}]),
    },
  });

  controller.setState({
    OrderSheetV4Sequence: {
      sequenceIndex: "Action",
      encounterId: params.encounterId ?? null,
      query: "",
      results: [],
      lines: [],
      editing: [],
      doctorOptions: [],
      icd9Query: "",
      icd9Options: [],
      packages: [],
      sets: {},
    },
    errorMessage: {
      ...clearedSheetErrors(controller.getState().errorMessage),
      [BTN_ACTS.SEARCH]: null,
    },
  });

  loadPackages(controller);
};

const loadPackages = async (controller: Parameters<Handler>[0]) => {
  const encounter = controller.getState().selectedEncounter as Record<string, any> | null;
  const extra = { division: controller.data.division, device: controller.data.device };

  const patient = encounter?.patient_id ?? encounter?.patient;

  if (!patient) {
    return;
  }

  const [res]: ApiResult = await PackageOrderWithItemList.get({
    params: { patient, limit: 100 },
    apiToken: controller.apiToken,
    extra,
  });

  const today = moment().startOf("day");
  const inPeriod = (order: Record<string, any>) => {
    const start = parseDate(order.package_start_date, true);
    const end = parseDate(order.package_end_date, true);

    return (
      ["BILLED", "PAID"].includes(order.status) && !start?.isAfter(today) && !end?.isBefore(today)
    );
  };

  const orders = ((res?.items ?? []) as Record<string, any>[]).filter(inPeriod);

  const packages: PackageEntitlement[] = orders.flatMap((order) =>
    ((order.items ?? []) as Record<string, any>[])
      .filter((item) => Number(item.quantity_left) > 0)
      .map((item) => ({
        product: item.product,
        packageName: order.product_name,
        left: Number(item.quantity_left),
      })),
  );

  const state = controller.getState();

  controller.setState({ OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, packages } });
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.ADD]: HandleAdd,
    [ACTIONS.EDIT]: HandleEdit,
    [ACTIONS.REMOVE]: HandleRemove,
    [ACTIONS.DROP_ORDER]: HandleDropOrder,
    [ACTIONS.CONFIRM]: HandleConfirm,
    [ACTIONS.SEARCH_ICD9]: HandleSearchIcd9,
    [ACTIONS.LOAD_ORDER]: HandleLoadOrder,
    [ACTIONS.LOAD_SETS]: HandleLoadSets,
    [ACTIONS.SAVE_SET]: HandleSaveSet,
    [ACTIONS.ADD_ITEMS]: HandleAddItems,
    [ACTIONS.RESOLVE_ADR]: HandleResolveAdr,
    [ACTIONS.RESOLVE_INTERACTION]: HandleResolveInteraction,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  let state = controller.getState();

  const encounterId = state.OrderSheetV4Sequence?.encounterId;
  const query = params.query.trim();

  if (!encounterId) {
    return;
  }

  controller.setState({
    OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, query, results: [] },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.SEARCH]: null },
  });

  const extra = { division: controller.data.division, device: controller.data.device };
  const common = { apiToken: controller.apiToken, extra };

  const productKinds = params.kinds.filter((kind) => PRODUCT_KINDS[kind]);

  const [[productRes, productError], [supplyRes, supplyError]] = await Promise.all([
    productKinds.length > 0
      ? ProductForOrderList.get({
          ...common,
          params: {
            search: query,
            encounter: encounterId,
            kinds: productKinds.map((kind) => PRODUCT_KINDS[kind]).join(","),
            limit_per_kind: SEARCH_LIMIT,
          },
        })
      : [null, null],
    params.kinds.includes("supply")
      ? SupplyList.list({
          ...common,
          params: { keyword: query, limit: SEARCH_LIMIT, encounter: encounterId },
        })
      : [null, null],
  ]);

  const products: Record<string, any>[] = productRes?.items ?? [];

  const results = params.kinds.map((kind): CatalogItem[] => {
    if (kind === "note") {
      return NOTE_ITEM_NAME.toLowerCase().includes(query.toLowerCase())
        ? [{ key: "note_0", kind: "note", id: null, name: NOTE_ITEM_NAME, price: 0, raw: {} }]
        : [];
    }

    if (kind === "supply") {
      return toCatalog("supply", supplyRes?.items, (item) => item.price_unit);
    }

    return toCatalog(
      kind,
      products.filter((item) => item.kind === PRODUCT_KINDS[kind]),
      (item) => item.unit_price,
    );
  });

  const drugIds = results
    .flat()
    .filter((item) => item.kind === "drug" && item.id)
    .map((item) => item.id as number);
  const warnings = drugIds.length > 0 ? await checkAdr(controller, drugIds) : [];

  state = controller.getState();

  // ผลของคำค้นเก่าที่ตอบกลับช้ากว่าไม่ต้องแสดง
  if (state.OrderSheetV4Sequence?.query !== query) {
    return;
  }

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      results: results.flat(),
      adrResults: {
        ...state.OrderSheetV4Sequence?.adrResults,
        ...Object.fromEntries(warnings.map((warning) => [warning.product, warning])),
      },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
  });

  if (productError || supplyError) {
    SetErrorMessage(controller, { btnAction: BTN_ACTS.SEARCH, error: productError || supplyError });
  }
};

const HandleSearchIcd9: Handler<Params<Actions["SEARCH_ICD9"]>> = async (controller, params) => {
  let state = controller.getState();

  const query = params.query.trim();
  const method = /^\d/.test(query) ? "icdcode" : "icdterm";
  const value = method === "icdcode" ? query.replace(/\./g, "") : query;

  controller.setState({
    OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, icd9Query: query, icd9Options: [] },
  });

  if (value.length < 3) {
    return;
  }

  const [res] = await icd9cmKeyUp.post({
    method,
    data: { [method]: value },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  state = controller.getState();

  if (state.OrderSheetV4Sequence?.icd9Query !== query) {
    return;
  }

  const options: Icd9Option[] = ((res?.response ?? []) as Record<string, any>[])
    .slice(0, SEARCH_LIMIT)
    .map((item) => ({
      id: item.icd10_id,
      code: item.icdcode,
      term: item.icdterm,
      medterm: item.medterm ?? "",
      label: `${item.icdcode} ${item.icdterm}`,
    }));

  controller.setState({
    OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, icd9Options: options },
  });
};

const HandleLoadOrder: Handler<Params<Actions["LOAD_ORDER"]>> = async (controller, params) => {
  let state = controller.getState();

  const { kind, orderId } = params;
  const common = {
    pk: orderId,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  };

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_ORDER]: BLC_STATUS.LOADING },
  });

  const [res, error]: ApiResult = await retrieveOrder(params, common);

  state = controller.getState();

  if (error || !res) {
    SetErrorMessage(controller, {
      btnAction: BTN_ACTS.LOAD_ORDER,
      errorKey: sheetErrorKey(kind),
      error,
    });

    return;
  }

  const lines = toOrderLines(kind, res, params.prices ?? [], params.specificType);
  const others = (state.OrderSheetV4Sequence?.lines ?? []).filter(
    (line) => line.kind !== kind || line.orderId !== orderId,
  );

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      lines: [...others, ...lines],
      editing: [
        ...(state.OrderSheetV4Sequence?.editing ?? []).filter(
          (item) => item.kind !== kind || item.orderId !== orderId,
        ),
        kind === "drug"
          ? { kind, orderId, drugType: drugTypeOfOrder(params.specificType) }
          : { kind, orderId },
      ],
      doctorOptions: getDoctorOptions(controller),
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_ORDER]: BLC_STATUS.SUCCESS },
  });

  if (kind === "drug") {
    await loadDrugLines(controller, lines);
  }
};

const retrieveOrder = async (
  params: Params<Actions["LOAD_ORDER"]>,
  common: { pk: number; apiToken: string; extra: Record<string, unknown> },
): Promise<ApiResult> => {
  if (params.kind === "df") {
    return [params.fee ?? null, params.fee ? null : "ไม่พบรายการ DF", null];
  }

  if (params.kind === "drug") {
    return DRUG_ORDER_APIS[drugTypeOfOrder(params.specificType)].get(common);
  }

  const api = {
    imaging: ImagingOrderDetail,
    treatment: TreatmentOrderDetail,
    note: DoctorNoteOrderDetail,
    supply: SupplyOrderDetail,
  }[params.kind];

  return api.retrieve(common);
};

const HandleAdd: Handler<Params<Actions["ADD"]>> = (controller, params) => {
  HandleAddItems(controller, {
    sequence: SEQUENCE_NAME,
    action: ACTIONS.ADD_ITEMS,
    entries: [
      {
        key: params.item.key,
        kind: params.item.kind,
        name: params.item.name,
        price: Number(params.item.price || 0),
        quantity: 1,
        item: params.item,
      },
    ],
    isDoctor: params.isDoctor,
  });
};

const HandleAddItems: Handler<Params<Actions["ADD_ITEMS"]>> = async (controller, params) => {
  let state = controller.getState();

  const current = state.OrderSheetV4Sequence?.lines ?? [];
  const drugIds = params.entries
    .filter(
      (entry) =>
        entry.item?.kind === "drug" &&
        entry.item.id &&
        !current.some((line) => line.kind === "drug" && line.product === entry.item?.id),
    )
    .map((entry) => entry.item?.id as number);

  if (!params.adr && drugIds.length > 0) {
    const warnings = await checkAdr(controller, drugIds);

    if (warnings.length > 0) {
      state = controller.getState();

      controller.setState({
        OrderSheetV4Sequence: {
          ...state.OrderSheetV4Sequence,
          adrAlert: { entries: params.entries, isDoctor: params.isDoctor, warnings },
        },
      });

      return;
    }
  }

  const adrOf = new Map((params.adr?.warnings ?? []).map((warning) => [warning.product, warning]));
  const orderIds = drugIds.filter((id) => {
    const adr = adrOf.get(id);

    return !adr || (!adr.locked && params.adr?.choice === "order");
  });

  if (!params.interaction && orderIds.length > 0) {
    const others = current.flatMap((line) =>
      line.kind === "drug" && line.product ? [line.product] : [],
    );
    const [drugs, labs] = await checkInteraction(controller, orderIds, others);

    if (drugs.length > 0 || labs.length > 0) {
      state = controller.getState();

      controller.setState({
        OrderSheetV4Sequence: {
          ...state.OrderSheetV4Sequence,
          adrAlert: null,
          interactionAlert: {
            entries: params.entries,
            isDoctor: params.isDoctor,
            adr: params.adr,
            drugs,
            labs,
          },
        },
      });

      return;
    }
  }

  state = controller.getState();

  const lines = state.OrderSheetV4Sequence?.lines ?? [];
  const encounterDoctor = state.selectedEmr?.doctor;
  const doctor = getDoctorOptions(controller).find((item) => item.id === encounterDoctor);
  const now = Date.now();
  // เหตุผลยาตีกันใส่ให้ยาทุกตัวในใบคำสั่งที่อยู่ในกลุ่ม
  const withInteraction = (line: SheetLine): SheetLine => {
    const id = line.kind === "drug" ? line.product : null;
    const drugs = (params.interaction?.drugs ?? []).filter(
      (group) => !!id && group.members.includes(id),
    );
    const labs = (params.interaction?.labs ?? []).filter((lab) => !!id && lab.product === id);
    const reason = params.interaction?.reason ?? "";

    if (drugs.length === 0 && labs.length === 0) {
      return line;
    }

    return {
      ...line,
      fields: {
        ...line.fields,
        ...(drugs.length > 0 ? { interactionNote: reason } : {}),
        ...(labs.length > 0 ? { labInteractionNote: reason } : {}),
      },
      raw: { ...line.raw, interaction: { drugs, labs } },
    };
  };

  const added: SheetLine[] = [];

  for (const [index, entry] of params.entries.entries()) {
    const { item } = entry;

    if (!item) {
      continue;
    }

    const adr = item.kind === "drug" && item.id ? adrOf.get(item.id) : undefined;

    if (adr && (adr.locked || params.adr?.choice !== "order")) {
      continue;
    }

    const price = Number(item.price || 0);
    // ยาที่อยู่ในใบคำสั่งแล้วไม่ถามแพ้ยาซ้ำ
    const adrNote =
      item.kind === "drug"
        ? lines.find((line) => line.kind === "drug" && line.product === item.id)?.fields.adrNote
        : "";

    added.push({
      key: `${item.key}_${now + index}`,
      itemKey: item.key,
      kind: item.kind,
      product: item.id,
      name: item.name,
      quantity: entry.quantity,
      masterPrice: price,
      priceUnit: price,
      fields: {
        ...DEFAULT_FIELDS[item.kind],
        ...(item.kind === "df" && doctor?.employee ? { employee: String(doctor.employee) } : {}),
        ...(item.kind === "note" && !params.isDoctor ? { onBehalf: "Y" } : {}),
        ...entry.fields,
        ...(adrNote ? { adrNote } : {}),
        ...(adr ? { adrNote: params.adr?.reason ?? "" } : {}),
      },
      raw: adr ? { ...item.raw, adr } : item.raw,
    });
  }

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      lines: [...lines, ...added].map(withInteraction),
      adrAlert: null,
      interactionAlert: null,
      groupNotice: null,
      doctorOptions: getDoctorOptions(controller),
    },
  });

  const drugs = added.map(withInteraction).filter((line) => line.kind === "drug");

  if (drugs.length <= 0) {
    return;
  }

  await loadDrugLines(controller, drugs);
  await checkDrugGroups(controller, drugs);
  await loadDoseRanges(
    controller,
    drugs.map((line) => line.key),
  );
};

const HandleResolveAdr: Handler<Params<Actions["RESOLVE_ADR"]>> = async (controller, params) => {
  const state = controller.getState();

  const alert = state.OrderSheetV4Sequence?.adrAlert;

  if (!alert) {
    return;
  }

  if (params.choice === "cancel") {
    controller.setState({
      OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, adrAlert: null },
    });

    return;
  }

  await HandleAddItems(controller, {
    sequence: SEQUENCE_NAME,
    action: ACTIONS.ADD_ITEMS,
    entries: alert.entries,
    isDoctor: alert.isDoctor,
    adr: { warnings: alert.warnings, choice: params.choice, reason: params.reason },
  });
};

const HandleResolveInteraction: Handler<Params<Actions["RESOLVE_INTERACTION"]>> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  const alert = state.OrderSheetV4Sequence?.interactionAlert;

  if (!alert) {
    return;
  }

  if (params.choice === "cancel") {
    controller.setState({
      OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, interactionAlert: null },
    });

    return;
  }

  await HandleAddItems(controller, {
    sequence: SEQUENCE_NAME,
    action: ACTIONS.ADD_ITEMS,
    entries: alert.entries,
    isDoctor: alert.isDoctor,
    adr: alert.adr,
    interaction: { drugs: alert.drugs, labs: alert.labs, reason: params.reason ?? "" },
  });
};

/** ค่าเริ่มต้นจาก master ยา และราคาต่อหน่วยจาก estimate */
const loadDrugLines = async (controller: Parameters<Handler>[0], drugs: SheetLine[]) => {
  const extra = { division: controller.data.division, device: controller.data.device };

  const details: ApiResult[] = await Promise.all(
    drugs.map((line) =>
      DrugDetail.retrieve({ pk: line.product, apiToken: controller.apiToken, extra }),
    ),
  );

  const solvents = await Promise.all(
    details.map(([detail]) => loadSolventNames(controller, detail)),
  );

  let state = controller.getState();

  const loaded = drugs.map((line, index): SheetLine => {
    const detail: Record<string, any> = details[index][0] ?? {};
    const value = (key: string) => (detail[key] == null ? "" : String(detail[key]));

    return {
      ...line,
      raw: { ...line.raw, detail, solvents: solvents[index] },
      fields: {
        ...line.fields,
        dose:
          line.fields.dose || (Number(detail.default_unit_dose) ? value("default_unit_dose") : ""),
        unit: line.fields.unit || value("default_dosage_unit"),
        route: line.fields.route || value("default_route"),
        site: line.fields.site || value("default_site"),
        frequency: line.fields.frequency || value("default_frequency"),
        prn: line.fields.prn || prnText(controller, detail.default_prn),
        duration:
          line.fields.duration ||
          (Number(detail.default_duration) ? value("default_duration") : ""),
      },
    };
  });

  const frequencies = getFrequencies(controller);
  const withQty = loaded.map((line) => withDrugQuantity(line, frequencies));

  const [estimate]: ApiResult = await DrugOrderEstimateView.post({
    apiToken: controller.apiToken,
    data: {
      items: withQty.map((line) => toDrugItem({ ...line, quantity: line.quantity || 1 })),
      drug_type: "HOME_OPD",
      encounter: state.selectedEncounter?.id,
      emr: state.selectedEmr?.id,
      progression_cycle: state.selectedProgressCycle?.id,
    },
    extra,
  });

  const prices = new Map<number, number>(
    ((estimate?.items ?? []) as Record<string, any>[]).map((item) => [
      item.product,
      Number(item.price_unit || 0),
    ]),
  );

  const updates = new Map(
    withQty.map((line) => {
      const price = prices.get(line.product ?? 0) ?? line.priceUnit;

      return [line.key, { ...line, masterPrice: price, priceUnit: price }];
    }),
  );

  state = controller.getState();

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      lines: (state.OrderSheetV4Sequence?.lines ?? []).map((line) => {
        const update = updates.get(line.key);

        if (!update) {
          return line;
        }

        // ค่าที่ผู้ใช้กรอกระหว่างโหลดยังคงไว้
        const fields = Object.fromEntries(
          Object.entries(update.fields).map(([key, value]) => [key, line.fields[key] || value]),
        );
        const merged = { ...update, fields };

        return withDrugQuantity(merged, frequencies);
      }),
    },
  });
};

const HandleLoadSets: Handler<Params<Actions["LOAD_SETS"]>> = async (controller, params) => {
  let state = controller.getState();

  const loaded = state.OrderSheetV4Sequence?.sets ?? {};
  const encounterId = state.OrderSheetV4Sequence?.encounterId;
  const ids = params.ids.filter((id) => !loaded[id]);
  const extra = { division: controller.data.division, device: controller.data.device };

  if (ids.length === 0) {
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_SETS]: BLC_STATUS.LOADING },
  });

  const details: ApiResult[] = await Promise.all(
    ids.map((id) =>
      OrderSetTemplateDetail.retrieve({ pk: id, apiToken: controller.apiToken, extra }),
    ),
  );

  const labCodes = [
    ...new Set(
      details.flatMap(([res]) => ((res?.lab_templates ?? []) as any[]).map((lab) => lab.code)),
    ),
  ];

  const labResults: ApiResult[] = await Promise.all(
    labCodes.map((code) =>
      CentralLabTestList.list({
        params: { lab_code: code, only_blood_bank: false, encounter: encounterId },
        apiToken: controller.apiToken,
        extra,
      }),
    ),
  );

  const labTests = new Map<string, LabTest>();

  for (const [index, [res]] of labResults.entries()) {
    const test = ((res?.items ?? []) as any[]).find((item) => item.lab_code === labCodes[index]);

    if (test) {
      labTests.set(labCodes[index], test);
    }
  }

  const sets: Record<number, SetEntry[]> = {};

  for (const [index, [res]] of details.entries()) {
    if (res) {
      sets[ids[index]] = toSetEntries(res, labTests);
    }
  }

  const hasError = details.some(([, error]) => error);

  state = controller.getState();

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      sets: { ...state.OrderSheetV4Sequence?.sets, ...sets },
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.LOAD_SETS]: hasError ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });
};

const HandleSaveSet: Handler<Params<Actions["SAVE_SET"]>> = async (controller, params) => {
  let state = controller.getState();

  const lines = state.OrderSheetV4Sequence?.lines ?? [];
  const kept = lines.filter((line) => SET_KINDS.includes(line.kind));
  const skipped = lines.filter((line) => !SET_KINDS.includes(line.kind)).map((line) => line.name);

  const missing = [
    ...params.labItems
      .filter((item) => !item.specimen_time)
      .map((item) => `${item.name}: ระบุ Specimen time`),
    ...kept
      .filter((line) => line.kind === "treatment" && !line.fields.icd9)
      .map((line) => `${line.name}: ระบุ ICD9`),
    ...(kept.length + params.labItems.length === 0 ? ["ไม่มีรายการที่ Order set เก็บได้"] : []),
  ];

  if (missing.length > 0) {
    params.onDone({ missing });

    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_SET]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.SAVE_SET]: null },
  });

  const [, error]: ApiResult = await OrderSetTemplateList.create({
    data: toSetTemplate(kept, params, controller.data.division) as any,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { btnAction: BTN_ACTS.SAVE_SET, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SAVE_SET]: BLC_STATUS.SUCCESS },
  });

  params.onDone({ count: kept.length + params.labItems.length, skipped });
};

const HandleEdit: Handler<Params<Actions["EDIT"]>> = (controller, params) => {
  const state = controller.getState();

  const frequencies = getFrequencies(controller);

  const lines = (state.OrderSheetV4Sequence?.lines ?? []).map((line) => {
    if (line.key !== params.key) {
      return line;
    }

    const edited = {
      ...line,
      quantity: params.quantity ?? line.quantity,
      priceUnit: params.priceUnit ?? line.priceUnit,
      fields: { ...line.fields, ...params.fields },
    };

    return edited.kind === "drug"
      ? withDoseWarning(withDrugQuantity(edited, frequencies), frequencies)
      : edited;
  });

  controller.setState({ OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, lines } });

  if (params.fields?.unit) {
    loadDoseRanges(controller, [params.key], lines);
  }
};

const HandleRemove: Handler<Params<Actions["REMOVE"]>> = async (controller, params) => {
  const state = controller.getState();

  const lines = (state.OrderSheetV4Sequence?.lines ?? []).filter((line) => line.key !== params.key);

  controller.setState({ OrderSheetV4Sequence: { ...state.OrderSheetV4Sequence, lines } });

  const duplicated = lines.filter((line) => line.raw?.groupDuplicates?.length > 0);

  if (duplicated.length > 0) {
    await checkDrugGroups(controller, duplicated, { notify: false, sheet: lines });
  }
};

// Order ที่ถูกยกเลิก: เอารายการและสถานะกำลังแก้ของ Order นั้นออกจากใบคำสั่ง
const HandleDropOrder: Handler<Params<Actions["DROP_ORDER"]>> = async (controller, params) => {
  const state = controller.getState();

  const isOther = (item: { kind: SheetKind; orderId?: number }) =>
    item.kind !== params.kind || item.orderId !== params.orderId;

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      lines: (state.OrderSheetV4Sequence?.lines ?? []).filter(isOther),
      editing: (state.OrderSheetV4Sequence?.editing ?? []).filter(isOther),
    },
  });
};

const HandleConfirm: Handler<Params<Actions["CONFIRM"]>> = async (controller, params) => {
  let state = controller.getState();

  const lines = state.OrderSheetV4Sequence?.lines ?? [];
  const editing = state.OrderSheetV4Sequence?.editing ?? [];
  const kinds = params.kinds.filter(
    (kind) =>
      lines.some((line) => line.kind === kind) || editing.some((item) => item.kind === kind),
  );

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.CONFIRM]: BLC_STATUS.LOADING },
    errorMessage: clearedSheetErrors(state.errorMessage),
  });

  const errors: Partial<Record<SheetKind, unknown>> = {};
  const saved: SheetKind[] = [];

  for (const kind of kinds) {
    const error = await saveKind(
      controller,
      kind,
      lines.filter((line) => line.kind === kind),
      editing.filter((item) => item.kind === kind),
    );

    if (error) {
      errors[kind] = error;
    } else {
      saved.push(kind);
    }
  }

  state = controller.getState();

  const hasError = Object.keys(errors).length > 0;

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      lines: (state.OrderSheetV4Sequence?.lines ?? []).filter((line) => !saved.includes(line.kind)),
      editing: (state.OrderSheetV4Sequence?.editing ?? []).filter(
        (item) => !saved.includes(item.kind),
      ),
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CONFIRM]: hasError ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });

  for (const [kind, error] of Object.entries(errors)) {
    SetErrorMessage(controller, {
      btnAction: BTN_ACTS.CONFIRM,
      errorKey: sheetErrorKey(kind as SheetKind),
      error,
    });
  }

  if (saved.length > 0) {
    /* #86608-2: บันทึกคำสั่งสำเร็จ (แลป/ยา/หัตถการ...) -> คอลัมน์ "คำสั่ง (Dr. Order)" ในตารางคิวต้องเห็นทันที */
    const sheetEncounterId =
      state.OrderSheetV4Sequence?.encounterId ??
      (state as { selectedEncounter?: { id?: number } }).selectedEncounter?.id;
    if (sheetEncounterId) {
      controller.handleEvent({
        message: "HandleRefreshEncounter",
        params: { encounterId: sheetEncounterId },
      });
    }
  }

  params.onDone?.(saved);
};

/** ====================================================  */
/**                         Save                          */
/** ====================================================  */
const saveKind = async (
  controller: Parameters<Handler>[0],
  kind: SheetKind,
  sheetLines: SheetLine[],
  editing: EditingOrder[] = [],
): Promise<unknown> => {
  const editIds = editing.map((item) => item.orderId);
  // รายการใหม่ประเภทเดียวกันรวมเข้า Order ที่กำลังแก้ (ยา: ชนิด Order เดียวกัน)
  const mergeTarget = (line: SheetLine) =>
    kind === "drug"
      ? editing.find((item) => item.drugType === drugTypeOf(line.fields))?.orderId
      : editIds[0];
  let lines =
    editIds.length > 0 && kind !== "note" && kind !== "df"
      ? sheetLines.map((line) =>
          line.orderId ? line : { ...line, orderId: mergeTarget(line) ?? line.orderId },
        )
      : sheetLines;
  const state = controller.getState();

  const encounter = state.selectedEncounter as Record<string, any> | null | undefined;
  const emr = state.selectedEmr as Record<string, any> | null | undefined;
  const extra = { division: controller.data.division, device: controller.data.device };
  const common = { apiToken: controller.apiToken, extra };

  if (!encounter?.id) {
    return "ไม่พบ Encounter";
  }

  const edits = new Map<number, SheetLine[]>();

  for (const line of lines) {
    if (line.orderId) {
      edits.set(line.orderId, [...(edits.get(line.orderId) ?? []), line]);
    }
  }

  for (const [orderId, orderLines] of edits) {
    const drugType = editing.find((item) => item.orderId === orderId)?.drugType;
    const error = await saveEdit(controller, kind, orderId, orderLines, drugType);

    if (error) {
      return error;
    }
  }

  // Order ที่ลบรายการออกหมด
  for (const orderId of editIds.filter((id) => !edits.has(id))) {
    const [, error] =
      kind === "df"
        ? await DoctorFeeOrderDetail.delete({
            ...common,
            pk: orderId,
            extra: { ...extra, data: { remark: EMPTY_EDIT_NOTE } },
          })
        : await DoctorOrderCancel.put({
            ...common,
            pk: orderId,
            data: { action: "CANCEL", note: EMPTY_EDIT_NOTE },
          });

    if (error) {
      return error;
    }
  }

  lines = lines.filter((line) => !line.orderId);

  if (lines.length === 0) {
    return null;
  }

  if (kind === "imaging") {
    const [, error] = await ImagingOrderList.create({
      ...common,
      data: {
        action: "ORDER",
        encounter: encounter.id,
        emr: encounter.medical_record,
        is_emergency: lines.some((line) => line.fields.urgency === "STAT"),
        extra: {},
        order_items: lines.map((line) => ({
          product: line.product,
          quantity: line.quantity,
          eligibility_type: 1,
          urgency: line.fields.urgency,
          note: formatImagingNote(line.fields),
        })),
        order_status: 2,
      } as any,
    });

    return error;
  }

  if (kind === "treatment") {
    for (const line of lines) {
      const [, error] = await TreatmentOrderList.create({
        ...common,
        data: {
          action: "ORDER",
          emr: emr?.id ?? null,
          encounter: encounter.id,
          order_items: [{ product: line.product, quantity: line.quantity, eligibility_type: 1 }],
          core_procedure: toCoreProcedure(line, emr?.id),
          order_detail: line.fields.detail,
          equipment_detail: line.fields.equipment,
          to_be_performer: line.fields.performer,
          is_advance: false,
          is_out_perform_div: false,
          order_status: 2,
          progression_cycle: state.selectedProgressCycle?.id,
          show_alert: true,
        } as any,
      });

      if (error) {
        return error;
      }
    }

    return null;
  }

  if (kind === "drug") {
    for (const type of Object.keys(DRUG_ORDER_APIS) as DrugOrderType[]) {
      const group = lines.filter((line) => drugTypeOf(line.fields) === type);

      if (group.length === 0) {
        continue;
      }

      const [estimate, estimateError]: ApiResult = await DrugOrderEstimateView.post({
        ...common,
        data: {
          items: group.map(toDrugItem),
          drug_type: type,
          encounter: encounter.id,
          emr: emr?.id,
          progression_cycle: state.selectedProgressCycle?.id,
        },
      });

      if (estimateError) {
        return estimateError;
      }

      const [, error] = await DRUG_ORDER_APIS[type].post({
        ...common,
        data: {
          action: "REQUEST",
          encounter: encounter.id,
          emr: emr?.id,
          progression_cycle: state.selectedProgressCycle?.id,
          order_div: encounter.division || controller.data.division,
          order_by: encounter.approve_by || encounter.doctor || emr?.doctor || null,
          items: estimate?.items ?? [],
          claim_payload: {},
          qa_claim_id: null,
          data_changed: true,
          doctor_note: "",
        },
      });

      if (error) {
        return error;
      }
    }

    return null;
  }

  if (kind === "supply") {
    for (const type of ["HOME_OPD", ...SUPPLY_USES.map(([use]) => use)]) {
      const group = lines.filter((line) => supplyTypeOf(line) === type);

      if (group.length === 0) {
        continue;
      }

      const previews: ApiResult[] = await Promise.all(
        group.map((line) =>
          SupplyOrderItemPreviewM.retrieve({
            ...common,
            params: {
              change: "quantity_request",
              encounter: encounter.id,
              mode: line.raw.mode,
              product: line.product,
              product_issue: "",
              quantity_request: line.quantity,
              quantity_issue: "",
              quantity_postpone: "",
              eligibility_type: 1,
              qa_claim_id: 0,
              type,
            },
          }),
        ),
      );

      const previewError = previews.find(([, error]) => error)?.[1];

      if (previewError) {
        return previewError;
      }

      const [, error] = await SupplyOrderList.create({
        ...common,
        data: {
          action: "REQUEST",
          type,
          encounter: encounter.id,
          items: previews.map(([item]) => item),
        } as any,
      });

      if (error) {
        return error;
      }
    }

    return null;
  }

  if (kind === "note") {
    for (const line of lines) {
      const [, error] = await DoctorNoteOrderList.create({
        ...common,
        data: {
          action: "REQUEST",
          emr: emr?.id,
          detail: line.fields.detail,
          note: line.fields.reason ?? "",
          is_out_perform_div: false,
          out_perform_div: null,
          plan_item: null,
        } as any,
      });

      if (error) {
        return error;
      }
    }

    return null;
  }

  if (kind === "df") {
    const [preview, previewError] = await DoctorFeeOrderPreviewList.post({
      ...common,
      data: {
        items: lines.map((line) => ({
          encounter: encounter.id,
          quantity: line.quantity,
          doctor_fee_rule: line.product,
          doctor_fee_rule_display: line.name,
          service_code: line.raw.service_code,
          price_unit: line.masterPrice,
          compensation_price: line.priceUnit,
          received_employee: Number(line.fields.employee),
        })),
      },
    });

    if (previewError) {
      return previewError;
    }

    const [, error] = await DoctorFeeOrderCreateUpdateList.create({
      ...common,
      data: {
        items: (preview?.items ?? []).map((item: any) => ({ ...item, encounter: encounter.id })),
      } as any,
    });

    return error;
  }

  const [preview, previewError] = await MiscellaneousPreviewList.post({
    ...common,
    data: {
      items: lines.map((line) => ({
        encounter: encounter.id,
        product: line.product,
        quantity: line.quantity,
        eligibility_type: "TREATMENT",
        charges_date: formatDate(moment()),
      })),
    },
  });

  if (previewError) {
    return previewError;
  }

  const [, error] = await MiscellaneousOrderListCreate.post({
    ...common,
    data: {
      miscellaneous_orders: (preview?.items ?? []).map((item: any, index: number) => ({
        ...item,
        encounter: encounter.id,
        note: lines[index]?.fields.note ?? "",
        ...(lines[index] && lines[index].priceUnit !== lines[index].masterPrice
          ? { override_unit_price: lines[index].priceUnit }
          : {}),
      })),
    } as any,
  });

  return error;
};

const saveEdit = async (
  controller: Parameters<Handler>[0],
  kind: SheetKind,
  orderId: number,
  lines: SheetLine[],
  drugType: DrugOrderType = "HOME_OPD",
): Promise<unknown> => {
  const state = controller.getState();

  const encounter = state.selectedEncounter as Record<string, any> | null | undefined;
  const emr = state.selectedEmr as Record<string, any> | null | undefined;
  const common = {
    pk: orderId,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  };

  if (kind === "imaging") {
    const [, error] = await ImagingOrderDetail.patch({
      ...common,
      data: {
        action: "EDIT",
        encounter: encounter?.id,
        emr: encounter?.medical_record,
        is_emergency: lines.some((line) => line.fields.urgency === "STAT"),
        extra: lines[0].raw.orderExtra ?? {},
        order_items: lines.map((line) => ({
          id: line.orderItemId,
          product: line.product,
          quantity: line.quantity,
          eligibility_type: line.raw.eligibility_type ?? 1,
          urgency: line.fields.urgency,
          note: formatImagingNote(line.fields),
        })),
        order_status: 2,
      } as any,
    });

    return error;
  }

  if (kind === "treatment") {
    const [line] = lines;

    const [, error] = await TreatmentOrderDetail.update({
      ...common,
      data: {
        action: "EDIT",
        id: orderId,
        emr: emr?.id ?? null,
        encounter: encounter?.id,
        order_items: lines.map((item) => ({
          product: item.product,
          quantity: item.quantity,
          eligibility_type: item.raw.eligibility_type ?? 1,
        })),
        core_procedure: toCoreProcedure(line, emr?.id),
        order_detail: line.fields.detail,
        equipment_detail: line.fields.equipment,
        to_be_performer: line.fields.performer,
        is_advance: false,
        is_out_perform_div: false,
        order_status: 2,
        progression_cycle: state.selectedProgressCycle?.id,
        show_alert: true,
      } as any,
    });

    return error;
  }

  if (kind === "note") {
    const [line] = lines;

    const [, error] = await DoctorNoteOrderDetail.update({
      ...common,
      data: {
        action: "EDIT",
        emr: emr?.id,
        detail: line.fields.detail,
        note: line.fields.reason ?? "",
      } as any,
    });

    return error;
  }

  const order: Record<string, any> = lines.find((line) => line.raw.order)?.raw.order ?? {};

  if (kind === "drug") {
    const [estimate, estimateError]: ApiResult = await DrugOrderEstimateView.post({
      ...common,
      data: {
        items: lines.map(toDrugItem),
        drug_type: drugType,
        encounter: encounter?.id,
        emr: emr?.id,
        progression_cycle: state.selectedProgressCycle?.id,
      },
    });

    if (estimateError) {
      return estimateError;
    }

    const [, error] = await DRUG_ORDER_APIS[drugType].put({
      ...common,
      data: {
        ...order,
        action: "EDIT",
        data_changed: true,
        items: ((estimate?.items ?? []) as Record<string, any>[]).map((item, index) => ({
          ...item,
          id: lines[index]?.orderItemId,
        })),
      },
    });

    return error;
  }

  if (kind === "supply") {
    const previews: ApiResult[] = await Promise.all(
      lines.map((line) =>
        SupplyOrderItemPreviewM.retrieve({
          ...common,
          params: {
            change: "quantity_request",
            encounter: encounter?.id,
            mode: line.raw.mode,
            product: line.product,
            product_issue: "",
            quantity_request: line.quantity,
            quantity_issue: "",
            quantity_postpone: "",
            eligibility_type: 1,
            qa_claim_id: 0,
            type: order.type,
          },
        }),
      ),
    );

    const previewError = previews.find(([, error]) => error)?.[1];

    if (previewError) {
      return previewError;
    }

    const [, error] = await SupplyOrderDetail.update({
      ...common,
      data: {
        action: "EDIT",
        pk: orderId,
        code: order.code,
        doctor: order.doctor,
        encounter: order.encounter,
        encounter_number: order.encounter_number,
        order_div: order.order_div,
        order_perform_div: order.order_perform_div,
        patient_id: order.patient_id,
        status: order.status,
        type: order.type,
        items: previews.map(([item], index) => ({ ...item, id: lines[index]?.orderItemId })),
      } as any,
    });

    return error;
  }

  if (kind === "df") {
    const [line] = lines;

    const [preview, previewError] = await DoctorFeeOrderPreviewList.post({
      ...common,
      data: {
        items: [
          {
            encounter: encounter?.id,
            quantity: line.quantity,
            doctor_fee_rule: line.product,
            doctor_fee_rule_display: line.name,
            service_code: line.raw.service_code,
            price_unit: line.masterPrice,
            compensation_price: line.priceUnit,
            received_employee: Number(line.fields.employee),
          },
        ],
      },
    });

    if (previewError) {
      return previewError;
    }

    const [, error] = await DoctorFeeOrderCreateUpdateList.create({
      ...common,
      data: {
        items: ((preview?.items ?? []) as Record<string, any>[]).map((item) => ({
          ...item,
          id: orderId,
          encounter: encounter?.id,
        })),
      } as any,
    });

    return error;
  }

  return null;
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */
const getDoctorOptions = (controller: Parameters<Handler>[0]): DoctorOption[] =>
  ((controller.data.masterData?.doctor ?? []) as Record<string, any>[])
    .filter((doctor) => doctor.employee)
    .map((doctor) => ({
      id: doctor.id,
      employee: doctor.employee,
      name: doctor.full_name ?? doctor.name_code,
      providers: (doctor.provider ?? []) as number[],
    }));

/** PRN ของยาเก็บเป็นข้อความ ค่าเริ่มต้นใน Master ของยาเป็น id */
const prnText = (controller: Parameters<Handler>[0], id: unknown) =>
  ((controller.data.masterData?.prn ?? []) as Record<string, any>[]).find((item) => item.id === id)
    ?.text ?? "";

const getFrequencies = (controller: Parameters<Handler>[0]) =>
  new Map<string, number>(
    ((controller.data.masterData?.frequency ?? []) as Record<string, any>[]).map((item) => [
      String(item.id),
      Number(item.dose_per_day || 0),
    ]),
  );

/** ขนาดยาสูงสุดต่อวันจาก Regimen (calculate-dose + dose-range) ของบรรทัดยาที่หน่วยเปลี่ยน */
const loadDoseRanges = async (
  controller: Parameters<Handler>[0],
  keys: string[],
  lines: SheetLine[] = controller.getState().OrderSheetV4Sequence?.lines ?? [],
) => {
  const state = controller.getState();
  const encounter = state.selectedEncounter?.id;
  const extra = { division: controller.data.division, device: controller.data.device };

  const targets = lines.filter(
    (line) =>
      keys.includes(line.key) &&
      line.kind === "drug" &&
      line.product &&
      line.fields.unit &&
      line.raw.doseRange?.unit !== line.fields.unit,
  );

  if (!encounter || targets.length === 0) {
    return;
  }

  const ranges = await Promise.all(
    targets.map(async (line): Promise<DoseRange | null> => {
      const [res]: ApiResult = await DrugDosingRecommendationList.list({
        params: { drug: line.product, encounter },
        apiToken: controller.apiToken,
        extra,
      });

      const picked = pickRegimen(res?.items ?? []);

      if (!picked) {
        return null;
      }

      const { regimen, weight } = picked;

      const [range]: ApiResult = await DrugDosingRecommendationRangeList.post({
        data: {
          regimen: regimen.id,
          drug: line.product,
          weight,
          unit: Number(line.fields.unit),
        },
        apiToken: controller.apiToken,
        extra,
      });

      if (!range || range.error?.length) {
        return null;
      }

      return {
        unit: line.fields.unit,
        maxPerDay: range.max_per_day == null ? null : Number(range.max_per_day),
        unitName: range.unit_of_result_name_en ?? "",
      };
    }),
  );

  const rangeOf = new Map(targets.map((line, index) => [line.key, ranges[index]]));
  const frequencies = getFrequencies(controller);
  const latest = controller.getState();

  controller.setState({
    OrderSheetV4Sequence: {
      ...latest.OrderSheetV4Sequence,
      lines: (latest.OrderSheetV4Sequence?.lines ?? []).map((line) =>
        rangeOf.has(line.key)
          ? withDoseWarning(
              {
                ...line,
                raw: {
                  ...line.raw,
                  doseRange: rangeOf.get(line.key) ?? { unit: line.fields.unit, maxPerDay: null },
                },
              },
              frequencies,
            )
          : line,
      ),
    },
  });
};

/** ยาที่ผู้ป่วยแพ้ (status LOCKED / NOTED / PAUSED) */
const checkAdr = async (
  controller: Parameters<Handler>[0],
  drugIds: number[],
): Promise<AdrWarning[]> => {
  const encounter = controller.getState().selectedEncounter as Record<string, any> | null;
  const patient = encounter?.patient_id ?? encounter?.patient;

  if (!patient) {
    return [];
  }

  const [res]: ApiResult = await AdverseReactionCheckListView.get({
    params: { patient, drugs: drugIds.join(",") },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division, device: controller.data.device },
  });

  return ((res ?? []) as Record<string, any>[])
    .filter((item) => [1, 2, 3].includes(item.status))
    .map((item) => ({
      product: item.drug_id,
      name: item.drug_name,
      adversary: /^ADR: (.*)$/m.exec(item.notes ?? "")?.[1]?.trim() || item.drug_name,
      notes: (item.notes ?? "")
        .split("\n")
        .filter((row: string) => !row.endsWith(": -"))
        .join("\n"),
      locked: item.status === 1,
    }));
};

/** ยาตีกันกับยาในใบคำสั่ง และยาที่มีผลกับผลแลปของผู้ป่วย */
const checkInteraction = async (
  controller: Parameters<Handler>[0],
  drugIds: number[],
  others: number[],
): Promise<[DrugInteraction[], DrugLabInteraction[]]> => {
  const encounter = controller.getState().selectedEncounter as Record<string, any> | null;
  const patient = encounter?.patient_id ?? encounter?.patient;
  const extra = { division: controller.data.division, device: controller.data.device };

  const [[drugRes], [labRes]]: ApiResult[] = await Promise.all([
    DrugInteractionCheck.list({
      params: { patient, drugs: [...new Set([...drugIds, ...others])].join(",") },
      apiToken: controller.apiToken,
      extra,
    }),
    patient
      ? DrugLabInteractionCheck.get({
          params: { patient, drugs: drugIds.join(",") },
          apiToken: controller.apiToken,
          extra,
        })
      : [null],
  ]);

  const drugs = ((drugRes?.items ?? []) as DrugInteractionGroupSerializer[])
    .filter((item) => (item.members ?? []).some((id: number) => drugIds.includes(id)))
    .map((item) => ({
      id: item.id ?? 0,
      members: item.members ?? [],
      names: item.members_name ?? [],
      severity: item.severity,
      affectSummary: item.affect_summary ?? "",
      management: item.management ?? "",
    }));

  const labs = ((labRes?.items ?? []) as Record<string, any>[]).map((item) => ({
    product: item.product_id,
    name: item.drug_name ?? "",
    summary: item.lab_warning_summary ?? "",
    detail: item.detail ?? "",
  }));

  return [drugs, labs];
};

/** ชื่อ Solvent ของยาจาก master (default_solvent + limited_solvents) */
const loadSolventNames = async (
  controller: Parameters<Handler>[0],
  detail?: DrugSerializer,
): Promise<string[]> => {
  const ids = (detail?.limited_solvents ?? []).filter((id) => id !== detail?.default_solvent);

  const limited: ApiResult[] = await Promise.all(
    ids.map((id) =>
      DrugDetail.retrieve({
        pk: id,
        apiToken: controller.apiToken,
        extra: { division: controller.data.division, device: controller.data.device },
      }),
    ),
  );

  return [
    detail?.default_solvent_name,
    ...limited.map(([solvent]) => solvent?.label_name ?? solvent?.full_name),
  ].filter(Boolean);
};

/** ยาซ้ำกลุ่มกับยาอื่นในใบคำสั่งและคำสั่งยาของ encounter */
const checkDrugGroups = async (
  controller: Parameters<Handler>[0],
  checked: SheetLine[],
  options: { notify?: boolean; sheet?: SheetLine[] } = {},
) => {
  const encounter = controller.getState().selectedEncounter as Record<string, any> | null;

  if (!encounter?.id) {
    return;
  }

  const messages: string[] = [];
  const found: Record<string, GroupDuplicate[]> = {};

  for (const line of checked) {
    const sheet = options.sheet ?? controller.getState().OrderSheetV4Sequence?.lines ?? [];
    const others = sheet.filter((item) => item.kind === "drug" && item.key !== line.key);

    const [, error]: ApiResult = await CheckDrugDuplicateDrugGroupView.post({
      data: {
        encounter: encounter.id,
        selected_drug: line.product,
        current_drug_order_type: drugTypeOf(line.fields),
        current_order_items: others.map(toDrugItem),
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division, device: controller.data.device },
    });

    if (error?.code !== "DUPLICATE_DRUG_GROUP") {
      continue;
    }

    for (const item of error.duplicate_list ?? []) {
      const duplicate = parseGroupDuplicate(item.duplicated_detail ?? "");

      if (!duplicate) {
        continue;
      }

      found[line.key] = [...(found[line.key] ?? []), duplicate];
      messages.push(
        `ซ้ำกลุ่มยา ${duplicate.group}: ${shortName(line.name)} กับ ${duplicate.names.join(", ")} · ตรวจสอบว่าจำเป็นต้องใช้ทั้งสองตัว`,
      );
    }
  }

  const keys = new Set(checked.map((line) => line.key));
  const state = controller.getState();

  controller.setState({
    OrderSheetV4Sequence: {
      ...state.OrderSheetV4Sequence,
      ...(options.notify !== false && messages.length > 0
        ? { groupNotice: messages.join(" | ") }
        : {}),
      lines: (state.OrderSheetV4Sequence?.lines ?? []).map((line) =>
        keys.has(line.key)
          ? { ...line, raw: { ...line.raw, groupDuplicates: found[line.key] ?? [] } }
          : line,
      ),
    },
  });
};
